import React from 'react';
import styled, { css } from 'styled-components';

import {
  confidenceBandFor,
  ConfidenceBand,
  HANDOFF_CONFIDENCE_MINIMUM,
  normaliseConfidenceBand
} from 'containers/Intelligence/data/confidence';
import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { reasonCopyKey } from '../reasonKey';
import { statusPill, statusPillLabel, STATUS_PILL_HUES, StatusPillTone } from '../statusPill';
import { visuallyHidden } from '../visuallyHidden';

/**
 * PLA-8828: the governed reliability score the Customer 360 wire carries on 15 metrics
 * (`confidence_score`, `confidence_band`, `confidence_limiting_reason`, `confidence_cap_applied`,
 * `handoff_confidence_ok`) and, on Product Readiness rows, as `composite_confidence` +
 * `handoff_confidence_ok`.
 *
 * The score measures data completeness, source quality, freshness, entity matching and calculation
 * quality — NOT whether the reading is good or bad. Metrics Catalogue, `Calculation & Governance
 * Rules` row 26.
 *
 * This component RENDERS a server-supplied score and band. G32 of the same sheet is explicit that
 * "FE must render the server-provided card score/band and must not independently recalculate it",
 * so nothing here derives a score from components, and nothing averages metric scores into a card
 * score. The thresholds below are used for ONE thing: naming a band when the server sent a score
 * but no band string.
 *
 * Distinct from the sibling `ConfidenceBadge`, which is Layer 1's card-level badge drawn to the
 * measured 36px Figma box and keyed on the parsed `CardConfidence` shape. This one takes plain
 * props so it can sit on a metric tile as well as a card header.
 */

/**
 * The band vocabulary the catalogue defines, and the thresholds behind it, both owned by
 * `data/confidence.ts` — the parser resolves the same four words off the wire, and two definitions
 * of a governed threshold are two things that can drift apart. Re-exported here so a consumer that
 * only knows about the badge still reaches them.
 */
export type MetricConfidenceBand = ConfidenceBand;

export type MetricConfidencePlacement = 'tile' | 'cardHeader';

export interface IMetricConfidenceBadgeProps {
  /** 0.00–1.00. `0` is a real, publishable score and still renders. */
  score?: number | null;
  /** The server's own band string, preferred over deriving one from the score. */
  band?: string | null;
  /** A reason CODE, never prose — rendered only when it maps to a translated string. */
  limitingReason?: string | null;
  /** The governed 0.59 critical cap: the score was limited, not measured. */
  capApplied?: boolean | null;
  /** Whether the score clears the 0.60 Growth Planner handoff minimum. */
  handoffOk?: boolean | null;
  placement?: MetricConfidencePlacement;
  className?: string;
}

/**
 * Verbatim from the catalogue: "High >=0.80; Medium 0.60-0.79; Low 0.40-0.59; Insufficient <0.40.
 * Handoff minimum 0.60." Exported so a consumer reads the governed numbers from one place instead
 * of restating them — but see the file note: these NAME a band, they never produce a score.
 */
export {
  CONFIDENCE_BAND_FLOORS,
  HANDOFF_CONFIDENCE_MINIMUM
} from 'containers/Intelligence/data/confidence';

/**
 * A band is a quality scale, not a verdict on the reading, so the hues come from the section's
 * existing `STATUS_PILL_HUES` rather than from a confidence-specific palette. `insufficient` takes
 * the neutral: an unscored metric is not a failed one.
 */
const BAND_TONES: Record<MetricConfidenceBand, StatusPillTone> = {
  high: 'positive',
  medium: 'warning',
  low: 'negative',
  insufficient: 'neutral'
};

const BAND_LABEL_KEYS: Record<MetricConfidenceBand, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  insufficient: 'Insufficient'
};

const I18N_GROUP = `${TCustomerInsights}.ConfidenceGovernance`;

// The bands are written to two decimals throughout the catalogue (">=0.80", "0.60-0.79"), so the
// reading is pinned to two as well: an 0.8 that prints as "0.8" reads as a different number from
// the threshold it is being compared against. `en-US` matches the app-wide `utils/formatters`.
const scoreFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/**
 * `confidence_limiting_reason` arrives as a code, and `reasonCopyKey` is the shared rule for turning
 * one into copy: keyed only if it is code-shaped, rendered only if the `Reasons` group answers, and
 * otherwise omitted rather than printed as an identifier.
 */
const REASONS_GROUP = `${I18N_GROUP}.Reasons`;

const Root = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md section 5). */
  && > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

const Pill = styled.span<{ $hue: string; $placement: MetricConfidencePlacement }>`
  ${statusPill}

  ${({ $placement }) =>
    $placement === 'cardHeader' &&
    css`
      padding: 0 ${tokens.space['12']};
      font-size: ${tokens.type['confidence-badge-size']};
      font-weight: ${tokens.type['confidence-badge-weight']};
      line-height: ${tokens.type['confidence-badge-line']};
    `}

  ${({ $placement }) =>
    $placement === 'tile' &&
    css`
      padding: 0 ${tokens.space['8']};
    `}
`;

const PillText = styled.span`
  ${statusPillLabel}
`;

const Note = styled.span`
  color: ${tokens.color['text-caption']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
`;

const HiddenNote = styled.span`
  ${visuallyHidden}
`;

const MetricConfidenceBadge: React.FC<IMetricConfidenceBadgeProps> = ({
  score,
  band,
  limitingReason,
  capApplied,
  handoffOk,
  placement = 'tile',
  className
}) => {
  const { t } = useMultiLanguageTranslation();

  const numericScore = typeof score === 'number' && Number.isFinite(score) ? score : null;
  const serverBand = typeof band === 'string' && band.trim() ? band.trim() : null;

  // Most metrics carry no confidence at all; an empty badge would read worse than no badge.
  if (numericScore === null && serverBand === null) {
    return null;
  }

  // Only when the server sent no band at all does the score name one — bucketing a band word this
  // vocabulary does not recognise would be the recalculation G32 forbids.
  const knownBand =
    normaliseConfidenceBand(serverBand) ??
    (serverBand === null ? confidenceBandFor(numericScore) : null);

  // A band string outside the catalogue's vocabulary is still the server's answer, so it is shown as
  // sent rather than dropped — on the neutral, because no tone can be claimed for an unknown band.
  const bandLabel = knownBand ? t(`${I18N_GROUP}.Bands.${BAND_LABEL_KEYS[knownBand]}`) : serverBand;
  const tone = knownBand ? BAND_TONES[knownBand] : 'neutral';

  const scoreText = numericScore === null ? null : scoreFormat.format(numericScore);

  const reasonKey = reasonCopyKey(REASONS_GROUP, limitingReason);
  const reasonText = reasonKey ? t(reasonKey) : null;

  const visibleNotes: string[] = [];

  // The cap line and the limiting reason are one fact stated twice: the catalogue gives a critical
  // reason its own 0.59 Confidence Cap, so the cap IS the reason. The generic line is kept only
  // where no reason resolved and it is the whole explanation the reader gets. PLA-8828.
  if (capApplied === true && !reasonText) {
    visibleNotes.push(t(`${I18N_GROUP}.CapApplied`));
  }

  if (reasonText) {
    visibleNotes.push(reasonText);
  }

  if (handoffOk === false) {
    visibleNotes.push(t(`${I18N_GROUP}.HandoffBelowMinimum`));
  }

  // The cleared handoff is the unremarkable case: printing it on every tile would bury the two
  // states a reader has to act on, so it is announced and available on hover instead of drawn.
  const clearedHandoffNote = handoffOk === true ? t(`${I18N_GROUP}.HandoffMeetsMinimum`) : null;

  return (
    <Root
      className={className}
      role="group"
      aria-label={t(`${I18N_GROUP}.Label`)}
      title={clearedHandoffNote ?? undefined}
      data-testid="metric-confidence-badge"
      data-band={knownBand ?? 'unknown'}
      data-placement={placement}
    >
      <Pill $hue={STATUS_PILL_HUES[tone]} $placement={placement}>
        <PillText data-testid="metric-confidence-badge-band">{bandLabel}</PillText>
        {scoreText ? (
          <>
            {/* The score is text, never a hue: WCAG 1.4.1 rules out the band colour carrying it. */}
            <PillText aria-hidden="true">{' · '}</PillText>
            <PillText data-testid="metric-confidence-badge-score">{scoreText}</PillText>
          </>
        ) : null}
      </Pill>

      {visibleNotes.map(note => (
        <Note key={note} data-testid="metric-confidence-badge-note">
          {note}
        </Note>
      ))}

      {clearedHandoffNote ? <HiddenNote>{clearedHandoffNote}</HiddenNote> : null}
    </Root>
  );
};

export default MetricConfidenceBadge;
